import React from 'react';
import Form from '../../forms/form';
import Box from '../../containers/box';
import Icon from '../../icon';
import Immutable from 'immutable';
import Button from '../../forms/button';
import Centerer from '../../containers/centerer';
import {ErrorList} from '../../../data/validation';
import styled from 'styled-components';

const EMPTY_LIST = Immutable.List([]);
const EMPTY_MAP = Immutable.Map({});

export default function FormRepeaterDefinition() {
	if (!(this instanceof FormRepeaterDefinition)) {
		return new FormRepeaterDefinition();
	}

	const me = this;
	let content;
	let contentMargin;
	let contentHeader;
	let contentHeaderCallback;
	let path = [];
	let readOnly;
	let isReadOnlyCallback;
	let deleteIconHint;
	let newItemButtonText;
	let columnCount;
	let entityName;
	let inactive;
	let inactiveCallback;
	let nonDeletable;
	let validator;
	let creationListener;

	let leftHeaderContent;
	let leftHeaderContentCallback;

	this.setLeftHeaderContent = function(value) {
		if (typeof value === 'function') {
			leftHeaderContentCallback = value;
			leftHeaderContent = null;
		} else {
			leftHeaderContent = value;
			leftHeaderContentCallback = null;
		}
		return this;
	};

	const getLeftHeaderContent = function(ctx) {
		if (leftHeaderContentCallback) {
			return leftHeaderContentCallback(ctx);
		} else {
			return leftHeaderContent;
		}
	};

	this.setValidator = function(value) {
		if (value && !value.isContextValidator) {
			throw 'Only context validator allowed in FormDefinition';
		}
		validator = value;
		return this;
	};

	this.onCreated = function(value) {
		creationListener = value;
		return this;
	};

	this.setNonDeletable = function(value) {
		nonDeletable = value;
		return this;
	};

	this.setEntityName = function(value) {
		entityName = value;
		return this;
	};

	this.getEntityName = function() {
		return entityName;
	};

	this.setColumnCount = function(value) {
		columnCount = value;
		return this;
	};

	this.setContent = function(value) {
		content = value;
		content._parentDefinition = this;
		return this;
	};

	this.getContent = function() {
		return content;
	};

	const isReadOnly = function(ctx) {
		return readOnly || (isReadOnlyCallback && isReadOnlyCallback(ctx));
	};

	this.setNewItemButtonText = function(value) {
		newItemButtonText = value;
		return this;
	};

	this.setDeleteIconHint = function(value) {
		deleteIconHint = value;
		return this;
	};

	this.setPath = function(value) {
		if (typeof value === 'string') {
			value = [ value ];
		}
		path = value || [];
		return this;
	};

	this.setReadOnly = function(value) {
		if (typeof value === 'function') {
			readOnly = false;
			isReadOnlyCallback = value;
		} else {
			readOnly = !!value;
			isReadOnlyCallback = null;
		}
		return this;
	};

	this.getContentHeader = function(ctx) {
		if (contentHeaderCallback) {
			return contentHeaderCallback(ctx);
		} else {
			return contentHeader;
		}
	};

	this.setContentHeader = function(value) {
		if (typeof value === 'function') {
			contentHeader = null;
			contentHeaderCallback = value;
		} else {
			contentHeader = value;
			contentHeaderCallback = null;
		}
		return this;
	};

	this.isInactive = function(ctx) {
		if (inactiveCallback) {
			return inactiveCallback(ctx);
		} else {
			return inactive;
		}
	};

	this.setInactive = function(value) {
		if (typeof value === 'function') {
			inactive = null;
			inactiveCallback = value;
		} else {
			inactive = value;
			inactiveCallback = null;
		}
		return this;
	};

	this.setContentMargin = function(value) {
		contentMargin = value;
		return this;
	}

	this.renderItem = function(ctx, key, basePath, data) {
		if (data.get('_deleted')) {
			return null;
		}
		ctx = ctx.withAppendedBasePath(basePath);
		const readOnly = ctx.isContextReadOnly();
		ctx.hasOuterForm = true;
		return (
			<Box
				key={key}
				header={this.getContentHeader(ctx)}
				padding="2px"
				inactive={this.isInactive(ctx)}
				leftHeaderContent={getLeftHeaderContent(ctx)}
				rightHeaderContent={readOnly || isReadOnly(ctx, data) || nonDeletable ? null : (
					<Icon
						name="delete"
						active
						hint={deleteIconHint}
						onMouseDown={e => {
							e.preventDefault();
							ctx.setData(data.set('_deleted', true));
							runChangeValidation(ctx);
						}}
					/>
				)}
			>
				{content.render(ctx)}
			</Box>
		)
	};

	const runChangeValidation = function(ctx, itemName) {
		let errors = new ErrorList();
		me.validateOnChange(ctx, errors, itemName);
		ctx.setRootErrors(errors.mergeWith(ctx.getRootErrors()));
	};

	const validate = function(errors, ctx, itemName) {
		if (validator) {
			validator.validate(errors, ctx, itemName);
		}
	};

	this.validateOnChange = function(ctx, errors, itemName) {
		ctx = ctx.withShortenedBasePath(1);
		validate(errors, ctx, itemName);
		if (this._parentDefinition && this._parentDefinition.validateOnChange) {
			this._parentDefinition.validateOnChange(ctx.withShortenedBasePath(path.length), errors, itemName)
		}
	};

	const validateAllInItem = function(ctx, errors, basePath) {
		ctx = ctx.withAppendedBasePath(basePath);
		content.validateAll(ctx, errors);
	}

	this.validateAll = function(ctx, errors) {
		ctx = ctx.withAppendedBasePath(path);
		validate(errors, ctx);
		const data = ctx.getData() || EMPTY_LIST;
		data.forEach((item, index) => {
			if (item.get('_deleted')) {
				return;
			}
			validateAllInItem(ctx, errors, index);
		});

	};

	const triggerOnCreated = function(ctx) {
		if (!creationListener) {
			return;
		}
		const data = ctx.getData() || EMPTY_LIST;
		if (data.size > 0) {
			ctx = ctx.withAppendedBasePath(data.size - 1);
			creationListener(ctx);
		}
	};

	this.render = function(ctx, key = 0) {
		ctx = ctx.withAppendedBasePath(path);
		ctx.setReadOnly(ctx.isReadOnly(path));
		const data = ctx.getData() || EMPTY_LIST;
		const readOnly = ctx.isContextReadOnly();
		return (
			<Form key={key} virtual>
				<Grid columnCount={columnCount || 1} gap={contentMargin}>
					{
						data.map((item, index) => this.renderItem(ctx, index, index, data.get(index)))
					}
					{
						newItemButtonText && !readOnly && (
							<Centerer>
								<Button
									text={newItemButtonText}
									onAction={() => {
										ctx.setData(data.push(EMPTY_MAP));
										triggerOnCreated(ctx);
									}}
								/>
							</Centerer>
						)
					}
				</Grid>
			</Form>
		);
	};
}

const Grid = styled.div`
	display: grid;
	grid-template-columns: repeat(${props => props.columnCount || 1}, 1fr);
	grid-gap: ${props => props.gap}
`;